import React, { useState } from 'react';
import axios from 'axios';
import Modal from '../Modal';
import HolderSelect from '../HolderSelect';
import { contractorAuth } from '../../lib/contractorAuth';
import {
  LIFECYCLE_LABEL, OPERATIONAL_LABEL, CONDITION_LABEL, holderLabel, holderKeyOf, holderFields, tagText } from '../../lib/assets';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const TITLES = {
  assign: 'Give it to someone', return: 'Take it back', move: 'Move it',
  status: 'Change its status', note: 'Add a note', retire: 'Retire it', lost: 'Mark it lost'
};

/**
 * One dialog for the lifecycle actions; each posts to its own endpoint.
 *
 * The actions are each their own thing on purpose: giving it to somebody,
 * taking it back, moving it, changing its status, noting something, retiring
 * it, marking it lost. Each is a different event in the device's life, and
 * each writes its own line of history on the server. Moved here unchanged
 * from pages/AssetDetail.js in the page redesign; `lost` is the one
 * addition, so the More menu can offer it beside the others.
 */
export default function ActionDialog({ kind, asset, meta, onClose, onDone }) {
  const [form, setForm] = useState({
    holder: '', at_home: false, location_id: asset.location_id || '', reason: '', text: '',
    lifecycle_status: asset.lifecycle_status, operational_status: asset.operational_status,
    condition: asset.condition, disposed: false, staff_only: false
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({
    ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value
  }));
  const places = (meta?.locations || []).filter((l) => l.active);
  const portal = Boolean(contractorAuth());

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    const body = {
      // A person (user_id) or a contractor (contractor_id, 093), from the one
      // With control; At home only for a person.
      assign: { ...holderFields(form.holder, { action: true }), reason: form.reason,
        ...(form.at_home && form.holder.startsWith('u:') ? { at_home: true }
          : form.location_id && form.location_id !== asset.location_id ? { location_id: form.location_id } : {}) },
      return: { reason: form.reason,
        ...(form.location_id && form.location_id !== asset.location_id ? { location_id: form.location_id } : {}) },
      move: { location_id: form.location_id || null, reason: form.reason },
      status: Object.fromEntries([
        ...['lifecycle_status', 'operational_status', 'condition']
          .filter((k) => form[k] !== asset[k]).map((k) => [k, form[k]]),
        ['reason', form.reason]]),
      note: { text: form.text, ...(form.staff_only && !portal ? { staff_only: true } : {}) },
      retire: { reason: form.reason, disposed: form.disposed },
      lost: { reason: form.reason }
    }[kind];
    try {
      await axios.post(`${API_BASE}/assets/${asset.id}/${kind}`, body, contractorAuth());
      onDone();
    } catch (err) {
      setError(err.response?.data?.error || 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  const placeSelect = (label, none) => (
    <div className="form-group">
      <label htmlFor="as-a-place">{label}</label>
      <select id="as-a-place" value={form.location_id} onChange={set('location_id')} disabled={form.at_home}>
        <option value="">{none}</option>
        {places.map((l) => <option key={l.id} value={l.id}>{l.path}</option>)}
      </select>
    </div>
  );
  const reason = (label = 'Why (optional)', required = false) => (
    <div className="form-group">
      <label htmlFor="as-a-reason">{label}</label>
      <input id="as-a-reason" value={form.reason} onChange={set('reason')} required={required} />
    </div>
  );

  return (
    <Modal
      title={`${TITLES[kind]} — ${tagText(asset)}`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" form="as-action-form" className={`btn ${kind === 'retire' || kind === 'lost' ? 'btn-danger' : 'btn-primary'}`}
            disabled={busy}>
            {busy ? 'Saving…' : kind === 'lost' ? 'Mark lost and locate it' : TITLES[kind]}
          </button>
        </>
      )}
    >
      <form id="as-action-form" onSubmit={submit}>
        {error && <p className="as-error" role="alert">{error}</p>}
        {kind === 'assign' && (
          <>
            <div className="form-group">
              <label htmlFor="as-a-user">Give it to</label>
              <HolderSelect id="as-a-user" meta={meta} value={form.holder} none="Choose…" required
                exclude={holderKeyOf(asset)}
                onChange={(key) => setForm((f) => ({ ...f, holder: key, at_home: key.startsWith('u:') ? f.at_home : false }))} />
            </div>
            {!form.holder.startsWith('c:') && (
              <label className="as-check">
                <input type="checkbox" checked={form.at_home} onChange={set('at_home')} />
                At their home (remote worker)
              </label>
            )}
            {form.holder.startsWith('c:') && (
              <p className="as-quiet" style={{ margin: '0 0 0.75rem' }}>A contractor holds it away from the office; the place below is where it is kept, if that is known.</p>
            )}
            {placeSelect('Kept at', 'Not recorded')}
            {reason()}
          </>
        )}
        {kind === 'return' && (
          <>
            <p>{`${holderLabel(asset) || 'Its holder'} hands it back and it goes into stock.`}</p>
            {placeSelect('Put it at', 'Not recorded')}
            {reason()}
          </>
        )}
        {kind === 'move' && (
          <>
            {placeSelect('Move it to', 'Nowhere in particular')}
            {reason()}
          </>
        )}
        {kind === 'status' && (
          <>
            <div className="form-group">
              <label htmlFor="as-a-life">Status</label>
              <select id="as-a-life" value={form.lifecycle_status} onChange={set('lifecycle_status')}>
                {(meta?.lifecycle || []).filter((s) => !['retired', 'disposed'].includes(s))
                  .map((s) => <option key={s} value={s}>{LIFECYCLE_LABEL[s]}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="as-a-op">Working?</label>
              <select id="as-a-op" value={form.operational_status} onChange={set('operational_status')}>
                {(meta?.operational || []).map((s) => <option key={s} value={s}>{OPERATIONAL_LABEL[s]}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="as-a-cond">Condition</label>
              <select id="as-a-cond" value={form.condition} onChange={set('condition')}>
                {(meta?.condition || []).map((s) => <option key={s} value={s}>{CONDITION_LABEL[s]}</option>)}
              </select>
            </div>
            {reason()}
          </>
        )}
        {kind === 'note' && (
          <>
            <div className="form-group">
              <label htmlFor="as-a-text">Note</label>
              <textarea id="as-a-text" rows={4} value={form.text} onChange={set('text')} required maxLength={4000} />
            </div>
            {!portal && (
              <label className="as-check">
                <input type="checkbox" checked={form.staff_only} onChange={set('staff_only')} />
                Staff only — a contractor looking at this device does not see it
              </label>
            )}
            <p className="as-quiet" style={{ margin: '0.5rem 0 0' }}>It goes on the Notes tab, and as a line in the history.</p>
          </>
        )}
        {kind === 'retire' && (
          <>
            <p>
              A retired device stays in the register with its history and tickets, but nobody can
              be given it or raise a ticket about it again.
            </p>
            {reason('Why is it being retired?', true)}
            <label className="as-check">
              <input type="checkbox" checked={form.disposed} onChange={set('disposed')} />
              It has physically left the firm (disposed of, sold, recycled)
            </label>
          </>
        )}
        {kind === 'lost' && (
          <>
            <p>Its agent reports a precise location while this is on, and the person at the machine sees nothing different.</p>
            {reason('Why is it lost? This is recorded with your name.', true)}
          </>
        )}
      </form>
    </Modal>
  );
}
